/**
 * /table/desk — the Concierge® Desk Verification Queue view.
 *
 * Doctrine 7 Jun 2026 evening (founder-mandated working-sheet flow):
 *
 *   > Wire each "Gate 4 hours × 3" line to open the desk queue
 *   > filtered to those venues. That turns the dossier into a live
 *   > working-sheet for the desk — exactly the human-closer flow
 *   > we want.
 *
 * The page reads `?draft_id=` and `?gates=` from the URL, calls
 * `GET /api/table/admin/queue` with those filters, and renders the
 * filtered queue with dispose actions (confirm / reject / hold /
 * pick-up). Each transition records WHO did it via a small
 * `by` field at the bottom — named-owner discipline.
 *
 * The desk member's flow:
 *   1. Founder shares the dossier; clicks "Gate 4 hours × 3" chip
 *   2. New tab opens this page with the 3 queue entries
 *   3. Desk picks one up (in_progress), calls the venue, confirms
 *      or rejects with a phone-notation note, moves to next
 *   4. The dossier preview can be re-rendered after the queue
 *      clears — verified_count + gates_pending_breakdown both
 *      update automatically.
 */
import React, { useEffect, useState, useCallback } from "react";
import { BACKEND_URL } from "@/lib/backend-url";

const API = BACKEND_URL + "/api";

// ── doctrine vocabulary mirror (matches table_audit_trail.GATE_LABELS) ──
const GATE_LABELS = {
    gate_1_location: "Gate 1 location & existence",
    gate_2_cuisine: "Gate 2 cuisine classification",
    gate_3_dietary: "Gate 3 vegetarian honesty",
    gate_4_hours: "Gate 4 hours recency",
    gate_5_phone: "Gate 5 phone provenance",
    gate_6_description: "Gate 6 description grounding",
};

function timeSince(iso) {
    if (!iso) return null;
    const then = new Date(iso).getTime();
    if (!then) return null;
    const ms = Date.now() - then;
    const mins = Math.round(ms / 60000);
    if (mins < 60) return `${mins} min`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 48) return `${hrs} h`;
    return `${Math.floor(hrs / 24)} d`;
}

function timeUntil(iso) {
    if (!iso) return null;
    const then = new Date(iso).getTime();
    if (!then) return null;
    const ms = then - Date.now();
    if (ms < 0) return `${Math.round(-ms / 60000)} min over`;
    const mins = Math.round(ms / 60000);
    if (mins < 60) return `${mins} min left`;
    return `${Math.floor(mins / 60)} h left`;
}

export default function TableDesk() {
    const params = new URLSearchParams(window.location.search);
    const draftId = params.get("draft_id") || "";
    const gates = params.get("gates") || "";
    const state = params.get("state") || "";

    const [rows, setRows] = useState([]);
    const [count, setCount] = useState(0);
    const [overdue, setOverdue] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [by, setBy] = useState(
        localStorage.getItem("table_desk_owner") || "",
    );

    const loadQueue = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const q = new URLSearchParams();
            if (draftId) q.set("draft_id", draftId);
            if (gates) q.set("gates", gates);
            if (state) q.set("state", state);
            q.set("limit", "200");
            const res = await fetch(`${API}/table/admin/queue?${q.toString()}`);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const j = await res.json();
            setRows(j.rows || []);
            setCount(j.count || 0);
            setOverdue(j.overdue || 0);
        } catch (e) {
            setError(String(e.message || e));
        } finally {
            setLoading(false);
        }
    }, [draftId, gates, state]);

    useEffect(() => { loadQueue(); }, [loadQueue]);

    const handleBy = (e) => {
        const v = e.target.value;
        setBy(v);
        localStorage.setItem("table_desk_owner", v);
    };

    const dispose = useCallback(async (entryId, newState, note) => {
        if (!by || !by.trim()) {
            setError("Enter your desk-owner handle first (e.g. priya@desk).");
            return;
        }
        try {
            const res = await fetch(
                `${API}/table/admin/queue/${encodeURIComponent(entryId)}/dispose`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ state: newState, by, note: note || null }),
                },
            );
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            await loadQueue();
        } catch (e) {
            setError(`dispose failed · ${e.message || e}`);
        }
    }, [by, loadQueue]);

    return (
        <main
            data-testid="table-desk-root"
            style={{
                fontFamily: "var(--t-serif-stack)",
                background: "var(--t-bg)",
                minHeight: "100vh",
                padding: "32px 24px",
            }}
        >
            <div style={{ maxWidth: 1080, margin: "0 auto" }}>
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <div
                            className="t-mono uppercase"
                            style={{ fontSize: 10, letterSpacing: "0.22em", color: "var(--t-ink3)" }}
                        >
                            Concierge® · desk · verification queue
                        </div>
                        <h1
                            className="t-serif"
                            style={{
                                fontSize: 30, fontWeight: 500,
                                color: "var(--t-ink)", marginTop: 4,
                            }}
                        >
                            {gates ? GATE_LABELS[gates] || gates : "All queued venues"}
                            {draftId ? (
                                <span style={{ fontSize: 14, color: "var(--t-ink3)", marginLeft: 12, fontStyle: "italic" }}>
                                    · draft {draftId}
                                </span>
                            ) : null}
                        </h1>
                    </div>
                    <div className="t-mono" style={{ fontSize: 11, color: "var(--t-ink2)", textAlign: "right" }}>
                        <div data-testid="desk-queue-count">
                            <b>{count}</b> in queue
                        </div>
                        {overdue > 0 ? (
                            <div data-testid="desk-overdue-count" style={{ color: "var(--t-brass)", marginTop: 2 }}>
                                <b>{overdue}</b> overdue
                            </div>
                        ) : null}
                    </div>
                </div>

                <div
                    style={{
                        background: "var(--t-card)",
                        border: "1px solid var(--t-line2)",
                        borderRadius: 12,
                        padding: "10px 14px",
                        marginBottom: 18,
                    }}
                >
                    <label
                        className="t-mono uppercase"
                        style={{ fontSize: 9, letterSpacing: "0.18em", color: "var(--t-ink3)", display: "block", marginBottom: 4 }}
                    >
                        signed in as
                    </label>
                    <input
                        data-testid="desk-by-input"
                        value={by}
                        onChange={handleBy}
                        placeholder="priya@desk · or your handle"
                        style={{
                            width: 320, padding: "5px 9px",
                            border: "1px solid var(--t-line2)", borderRadius: 6,
                            fontFamily: "var(--t-mono-stack)", fontSize: 12,
                            color: "var(--t-ink)", background: "var(--t-bg)",
                        }}
                    />
                </div>

                {error ? (
                    <div
                        data-testid="desk-error"
                        style={{
                            background: "rgba(180, 60, 60, 0.06)",
                            border: "1px solid rgba(180, 60, 60, 0.45)",
                            borderRadius: 12, padding: "10px 14px",
                            color: "#A33232", marginBottom: 16, fontSize: 13,
                        }}
                    >
                        {error}
                    </div>
                ) : null}

                {loading ? (
                    <div style={{ color: "var(--t-ink3)", fontStyle: "italic", padding: "20px 0" }}>
                        loading queue…
                    </div>
                ) : rows.length === 0 ? (
                    <div
                        data-testid="desk-queue-empty"
                        style={{
                            textAlign: "center",
                            color: "var(--t-ink3)",
                            padding: "40px 0",
                            fontStyle: "italic",
                        }}
                    >
                        Queue is clear for this filter.
                    </div>
                ) : (
                    <div className="space-y-3">
                        {rows.map((r) => <QueueRow key={r.entry_id || `${r.draft_id}:${r.venue_id}`} entry={r} dispose={dispose} />)}
                    </div>
                )}
            </div>
        </main>
    );
}

function QueueRow({ entry, dispose }) {
    const [note, setNote] = useState("");
    const stateColor = {
        queued: "var(--t-ink3)",
        in_progress: "var(--t-brass)",
        confirmed: "var(--t-verified)",
        rejected: "#A33232",
        held: "var(--t-ink3)",
    }[entry.state] || "var(--t-ink3)";
    return (
        <article
            data-testid={`desk-queue-row-${entry.entry_id || entry.venue_id}`}
            style={{
                background: "var(--t-card)",
                border: `1px solid ${entry.overdue ? "rgba(180, 60, 60, 0.5)" : "var(--t-line2)"}`,
                borderRadius: 12,
                padding: "14px 18px",
            }}
        >
            <div className="flex items-start justify-between gap-4">
                <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                        <h3 className="t-serif" style={{ fontSize: 18, fontWeight: 500, color: "var(--t-ink)" }}>
                            {entry.venue_name}
                        </h3>
                        <span className="t-mono" style={{ fontSize: 10, color: "var(--t-ink3)" }}>
                            {entry.city}{entry.country ? `, ${entry.country}` : ""}
                        </span>
                    </div>
                    <div className="t-mono" style={{ fontSize: 10, color: "var(--t-ink3)", marginTop: 4 }}>
                        gates: {(entry.gates_pending || []).join(" · ") || "—"}
                    </div>
                    {entry.reason ? (
                        <div className="t-serif" style={{ fontSize: 12, color: "var(--t-ink2)", marginTop: 6, fontStyle: "italic" }}>
                            reason: {entry.reason}
                        </div>
                    ) : null}
                </div>
                <div style={{ textAlign: "right", minWidth: 140 }}>
                    <div
                        className="t-mono uppercase"
                        data-testid={`desk-row-state-${entry.entry_id || entry.venue_id}`}
                        style={{ fontSize: 10, letterSpacing: "0.18em", color: stateColor, fontWeight: 700 }}
                    >
                        {entry.state}{entry.overdue ? " · overdue" : ""}
                    </div>
                    <div className="t-mono" style={{ fontSize: 10, color: "var(--t-ink3)", marginTop: 4 }}>
                        in {timeSince(entry.entered_at)} ago
                    </div>
                    <div className="t-mono" style={{ fontSize: 10, color: entry.overdue ? "var(--t-brass)" : "var(--t-ink3)" }}>
                        sla: {timeUntil(entry.sla_due_at)}
                    </div>
                    {entry.assigned_to ? (
                        <div className="t-mono" style={{ fontSize: 10, color: "var(--t-ink2)", marginTop: 4 }}>
                            owner: {entry.assigned_to}
                        </div>
                    ) : null}
                </div>
            </div>
            {entry.state === "queued" || entry.state === "in_progress" ? (
                <div style={{ marginTop: 12, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <input
                        data-testid={`desk-row-note-${entry.entry_id || entry.venue_id}`}
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="note · phoned, open · or reason for hold"
                        style={{
                            flex: 1, minWidth: 220, padding: "5px 9px",
                            border: "1px solid var(--t-line2)", borderRadius: 6,
                            fontFamily: "var(--t-mono-stack)", fontSize: 12,
                            color: "var(--t-ink)", background: "var(--t-bg)",
                        }}
                    />
                    {entry.state === "queued" ? (
                        <button
                            data-testid={`desk-row-pickup-${entry.entry_id || entry.venue_id}`}
                            type="button"
                            onClick={() => dispose(entry.entry_id, "in_progress", note)}
                            style={{
                                fontSize: 11, padding: "5px 12px",
                                background: "transparent",
                                color: "var(--t-brass)",
                                border: "1px solid var(--t-brass)", borderRadius: 18,
                                cursor: "pointer",
                            }}
                        >
                            pick up
                        </button>
                    ) : null}
                    <button
                        data-testid={`desk-row-confirm-${entry.entry_id || entry.venue_id}`}
                        type="button"
                        onClick={() => dispose(entry.entry_id, "confirmed", note)}
                        style={{
                            fontSize: 11, padding: "5px 12px",
                            background: "var(--t-verified, #5f8a3f)",
                            color: "#fff", border: "none", borderRadius: 18,
                            cursor: "pointer",
                        }}
                    >
                        confirm
                    </button>
                    <button
                        data-testid={`desk-row-hold-${entry.entry_id || entry.venue_id}`}
                        type="button"
                        onClick={() => dispose(entry.entry_id, "held", note)}
                        style={{
                            fontSize: 11, padding: "5px 12px",
                            background: "transparent",
                            color: "var(--t-ink2)",
                            border: "1px solid var(--t-line2)", borderRadius: 18,
                            cursor: "pointer",
                        }}
                    >
                        hold
                    </button>
                    <button
                        data-testid={`desk-row-reject-${entry.entry_id || entry.venue_id}`}
                        type="button"
                        onClick={() => dispose(entry.entry_id, "rejected", note)}
                        style={{
                            fontSize: 11, padding: "5px 12px",
                            background: "transparent",
                            color: "#A33232",
                            border: "1px solid rgba(180, 60, 60, 0.6)", borderRadius: 18,
                            cursor: "pointer",
                        }}
                    >
                        reject
                    </button>
                </div>
            ) : null}
            {(entry.dispositions && entry.dispositions.length > 0) ? (
                <div
                    className="t-mono"
                    style={{ fontSize: 10, color: "var(--t-ink3)", marginTop: 10, paddingTop: 8, borderTop: "1px solid var(--t-line2)" }}
                >
                    {entry.dispositions.map((d, i) => (
                        <div key={i} style={{ marginBottom: 2 }}>
                            <span style={{ color: "var(--t-ink2)" }}>{d.state}</span>
                            {" · "}{d.by}{" · "}{timeSince(d.at)} ago
                            {d.note ? <span style={{ color: "var(--t-ink2)" }}> · "{d.note}"</span> : null}
                        </div>
                    ))}
                </div>
            ) : null}
        </article>
    );
}
